import { useState, useEffect } from "react";

// ─── Media Query ─────────────────────────────────────────────────────────────

const QUERY = "(prefers-reduced-motion: reduce)";

/**
 * Returns `true` when the user has enabled the "Reduce motion" accessibility
 * setting in their operating system.
 *
 * This hook listens for changes to the `prefers-reduced-motion` media query
 * and re-renders the component when the preference changes (e.g. the user
 * toggles the setting while the app is open).
 *
 * ## Usage with Framer Motion
 *
 * Pass the result of this hook to Framer Motion's `initial`, `animate`, and
 * `transition` props to disable or simplify animations for users who have
 * requested reduced motion.
 *
 * @example
 * ```tsx
 * function AnimatedCard({ children }: { children: React.ReactNode }) {
 *   const prefersReducedMotion = usePrefersReducedMotion();
 *
 *   return (
 *     <motion.div
 *       initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
 *       animate={{ opacity: 1, y: 0 }}
 *       transition={
 *         prefersReducedMotion
 *           ? { duration: 0 }
 *           : { type: "spring", stiffness: 300, damping: 24 }
 *       }
 *     >
 *       {children}
 *     </motion.div>
 *   );
 * }
 * ```
 *
 * @example Conditionally skip layout animations
 * ```tsx
 * const prefersReducedMotion = usePrefersReducedMotion();
 *
 * <motion.div layout={!prefersReducedMotion}>
 *   {content}
 * </motion.div>
 * ```
 *
 * @example Use with AnimatePresence
 * ```tsx
 * const prefersReducedMotion = usePrefersReducedMotion();
 *
 * <AnimatePresence>
 *   {isVisible && (
 *     <motion.div
 *       initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, scale: 0.95 }}
 *       animate={{ opacity: 1, scale: 1 }}
 *       exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0.95 }}
 *       transition={{ duration: prefersReducedMotion ? 0 : 0.2 }}
 *     >
 *       {content}
 *     </motion.div>
 *   )}
 * </AnimatePresence>
 * ```
 *
 * @returns `true` if the user prefers reduced motion, `false` otherwise.
 */
export function usePrefersReducedMotion(): boolean {
  // Default to `false` (animations enabled) during SSR or when
  // `matchMedia` is not available.
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return false;
    }
    return window.matchMedia(QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return;
    }

    const mediaQuery = window.matchMedia(QUERY);

    // Handler for changes to the media query
    const handleChange = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    // Modern browsers support `addEventListener` on `MediaQueryList`.
    // Older browsers only support the deprecated `addListener`.
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
    } else if (mediaQuery.addListener) {
      // eslint-disable-next-line deprecation/deprecation
      mediaQuery.addListener(handleChange);
    }

    // Sync in case the value changed between initial render and effect
    setPrefersReducedMotion(mediaQuery.matches);

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", handleChange);
      } else if (mediaQuery.removeListener) {
        // eslint-disable-next-line deprecation/deprecation
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  return prefersReducedMotion;
}

// ─── Utility: reduced-motion-safe transition ─────────────────────────────────

/**
 * Helper that returns a Framer Motion `transition` object that respects
 * the user's reduced-motion preference.
 *
 * When reduced motion is preferred, the transition duration is set to `0`
 * (instant), effectively disabling the animation while still allowing
 * the final state to be applied.
 *
 * @param prefersReducedMotion - Result of `usePrefersReducedMotion()`.
 * @param transition - The desired transition when motion is allowed.
 * @returns A transition config — either the original or a zero-duration one.
 *
 * @example
 * ```tsx
 * const prefersReducedMotion = usePrefersReducedMotion();
 *
 * <motion.div
 *   animate={{ opacity: 1 }}
 *   transition={safeTransition(prefersReducedMotion, {
 *     type: "spring",
 *     stiffness: 300,
 *     damping: 24,
 *   })}
 * />
 * ```
 */
export function safeTransition<T extends Record<string, unknown>>(
  prefersReducedMotion: boolean,
  transition: T,
): T | { duration: number } {
  if (prefersReducedMotion) {
    return { duration: 0 } as { duration: number };
  }
  return transition;
}

// ─── Utility: reduced-motion-safe variants ───────────────────────────────────

/**
 * Returns Framer Motion animation variants that collapse to static values
 * when the user prefers reduced motion.
 *
 * @param prefersReducedMotion - Result of `usePrefersReducedMotion()`.
 * @param variants - An object with `hidden` and `visible` variant definitions.
 * @returns The original variants, or simplified no-animation variants.
 *
 * @example
 * ```tsx
 * const prefersReducedMotion = usePrefersReducedMotion();
 *
 * const variants = safeVariants(prefersReducedMotion, {
 *   hidden: { opacity: 0, y: 20 },
 *   visible: { opacity: 1, y: 0, transition: { type: "spring" } },
 * });
 *
 * <motion.div variants={variants} initial="hidden" animate="visible">
 *   Content
 * </motion.div>
 * ```
 */
export function safeVariants<
  V extends {
    hidden: Record<string, unknown>;
    visible: Record<string, unknown>;
  },
>(prefersReducedMotion: boolean, variants: V): V {
  if (prefersReducedMotion) {
    // Return variants where both states are identical (the visible state)
    // and transitions are instant. This means no visual animation occurs.
    const visible = { ...variants.visible, transition: { duration: 0 } };
    return {
      ...variants,
      hidden: visible,
      visible,
    };
  }
  return variants;
}

export default usePrefersReducedMotion;
